/**
 * The organization's rules: the list the rules panel and the audit log read, and each rule an
 * editor has open.
 *
 * A `rules` change frame reloads the list and the open rule it names. Another session sends
 * one, and so does a media_server that disables a rule by its limit. The frame also reads the
 * trigger count of its rule again, because the server that disables the rule and an AI reset
 * both change the count.
 */

import { atom, type WritableAtom } from 'jotai';
import { atomFamily } from 'jotai-family';

import { snapshotAtoms, snapshotMemberAtom } from '../snapshotAtoms';
import { api } from '@/api';
import type { RuleSnapshot, RulesSnapshot } from '@/types/agents';
import { type StreamInput, StreamInputKind } from '@/types/notifications';
import { PortalEvent } from '@/types/portal';
import { SnapshotCommand, type SnapshotWrite } from '@/types/snapshot';

import { ruleTriggerCountChangedAtom } from './ruleTriggerCountAtoms';

const NO_RULES: RulesSnapshot = { rules: [], isLoading: true, error: null };

const list = snapshotAtoms<RulesSnapshot>(NO_RULES, async (): Promise<RulesSnapshot> => (
    { rules: await api.listRules(), isLoading: false, error: null }
));

export const rulesAtom = list.value;
export const refreshRulesAtom = list.reload;

const NO_RULE: RuleSnapshot = { rule: null, isLoading: true, error: null };

/** One rule by id: read it for the snapshot, set it with a command. */
export const ruleAtom = atomFamily((id: string) => snapshotMemberAtom(
    snapshotAtoms<RuleSnapshot>(NO_RULE, async (): Promise<RuleSnapshot> => (
        { rule: await api.getRule(id), isLoading: false, error: null }
    )),
));

const NONE_OPEN: RuleSnapshot = { rule: null, isLoading: false, error: null };

/** What an editor with no rule reads; there is nothing to load, so a command has no effect. */
export const noRuleAtom: WritableAtom<RuleSnapshot, [SnapshotWrite<RuleSnapshot>], void> = atom(
    (): RuleSnapshot => NONE_OPEN,
    (_get, _set, _write: SnapshotWrite<RuleSnapshot>): void => undefined,
);

const RELOAD: SnapshotWrite<RuleSnapshot> = { command: SnapshotCommand.Reload };

/** What the notifications stream means for the rules. */
export const rulesStreamAtom = atom(null, (_get, set, input: StreamInput): void => {
    // The gap may have hidden a change frame.
    if (input.kind === StreamInputKind.Reopened) {
        set(refreshRulesAtom);
        for (const id of ruleAtom.getParams()) {
            set(ruleAtom(id), RELOAD);
        }
        return;
    }
    if (input.kind !== StreamInputKind.Frame) {
        return;
    }
    const { frame } = input;
    if (frame.type !== 'change' || frame.resource !== 'rules') {
        return;
    }
    set(refreshRulesAtom);
    set(ruleTriggerCountChangedAtom, frame.id);
    // A frame must not make a member for each rule it names: only an open rule has one.
    if ([...ruleAtom.getParams()].includes(frame.id)) {
        set(ruleAtom(frame.id), RELOAD);
    }
});

/** The list read or changed while the cloud was away is stale or failed until a reload. */
export const rulesPortalAtom = atom(null, (_get, set, event: PortalEvent): void => {
    if (event === PortalEvent.Answered) {
        set(refreshRulesAtom);
    }
});
